import { useEffect, useMemo, useState } from 'react';

type DisconnectRecord = {
  at: string;
  reason: string;
  context?: string;
};

type GroupedRow = {
  minuteKey: string; // YYYY-MM-DD HH:MM
  entries: DisconnectRecord[];
};

const STORAGE_KEY = 'playcast.peer.disconnectHistory';

const readHistory = (): DisconnectRecord[] => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: DisconnectRecord[] = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatMinuteKey = (isoOrHuman: string): string => {
  // We stored human-readable via toLocaleString; normalize to minute group using Date parsing
  const d = new Date(isoOrHuman);
  if (!isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  }
  // Fallback: return the original string minute precision
  return isoOrHuman.slice(0, 16);
};

export const DevDisconnects = () => {
  const [records, setRecords] = useState<DisconnectRecord[]>(readHistory());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setRecords(readHistory());
      }
    };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(() => setRecords(readHistory()), 1500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const grouped = useMemo<GroupedRow[]>(() => {
    const byMinute: Record<string, DisconnectRecord[]> = {};
    for (const rec of records) {
      const key = formatMinuteKey(rec.at);
      byMinute[key] = byMinute[key] || [];
      byMinute[key].push(rec);
    }
    const rows: GroupedRow[] = Object.entries(byMinute)
      .map(([minuteKey, entries]) => ({ minuteKey, entries }))
      .sort((a, b) => (a.minuteKey < b.minuteKey ? 1 : -1));
    return rows;
  }, [records]);

  const clearHistory = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setRecords([]);
    } catch (err) {
      console.error('[Marketing] Failed to clear disconnect history', err);
    }
  };

  return (
    <div className='mx-auto w-full max-w-5xl p-4 text-neutral-200'>
      <div className='mb-4 flex items-center justify-between'>
        <h1 className='text-2xl font-bold'>Disconnect Reasons</h1>
        <button onClick={clearHistory} className='rounded bg-red-600 px-3 py-1 text-white'>
          Clear
        </button>
      </div>

      {grouped.length === 0 ? (
        <div className='text-neutral-400'>No disconnects recorded yet.</div>
      ) : (
        <div className='space-y-3'>
          {grouped.map((row) => (
            <div key={row.minuteKey} className='overflow-hidden rounded border border-neutral-700 bg-neutral-900 text-neutral-100'>
              <div className='bg-neutral-800 px-3 py-2 text-sm font-medium'>
                {row.minuteKey} ({row.entries.length})
              </div>
              <div className='divide-y divide-neutral-800'>
                {row.entries.map((e, idx) => (
                  <div key={idx} className='grid grid-cols-1 gap-2 px-3 py-2 sm:grid-cols-12 sm:items-center'>
                    <div className='text-xs text-neutral-400 sm:col-span-3'>{e.at}</div>
                    <div className='break-words text-sm sm:col-span-6'>{e.reason}</div>
                    <div className='break-words text-xs text-neutral-400 sm:col-span-3'>{e.context || ''}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DevDisconnects;
